import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  HandleFinishParams,
  KEY_OPPORTUNITIES_ACTIVE_BASE_LIST,
  OpportunitiesItem,
  OPPORTUNITY_TYPE_ENUM,
  useGetOpportunitiesActiveBaseListInfiniteQuery,
  useSetFinishedOpportunityActiveBaseMutation,
  useSetOpportunityActiveBaseHighlightedMutation,
  useSetOpportunityActiveBaseUnHighlightedMutation,
  useSetUnFinishedOpportunityActiveBaseMutation
} from 'integration/resources/newOpportunities'
import { useBreakpointValue, useToast } from 'native-base'
import { Toast } from 'organisms'
import { useQueryClient } from 'react-query'
import { LIST_TYPE_ENUM } from 'src/screens/OrganizationScreen/OrganizationScreen.types'
import { calculateActiveFilterCount } from 'src/utils/filters'
import { LogEvent } from 'src/utils/logEvents'

import { UseActiveBase } from './types'

const mergeAllDataInfiniteQuery = (
  data: ReturnType<typeof useGetOpportunitiesActiveBaseListInfiniteQuery>['data']
): OpportunitiesItem[] => {
  return (
    data?.pages?.reduce<OpportunitiesItem[]>((previousValue, currentValue) => {
      return [...previousValue, ...(currentValue.data.data || [])]
    }, []) ?? []
  )
}

export const useActiveBase: UseActiveBase = ({ route, search, navigation }) => {
  const allFilters = useMemo(() => {
    return {
      public_rating: route?.params?.public_rating,
      segment: route?.params?.segment,
      network: route?.params?.network,
      subsidiary: route?.params?.subsidiary,
      user_std_code: route?.params?.user_std_code
    }
  }, [route?.params])

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchHook
  } = useGetOpportunitiesActiveBaseListInfiniteQuery(
    {
      per_page: 10,
      search: search ?? undefined,
      ...allFilters
    },
    undefined
  )

  const list = useMemo(() => mergeAllDataInfiniteQuery(data), [data])

  const totalItems = data?.pages[0].data.metadata.pagination.total ?? 0

  const fetchMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const [refetchLoading, setRefetchLoading] = useState(false)

  const queryClient = useQueryClient()

  const refetch = useCallback(() => {
    setRefetchLoading(true)

    setTimeout(() => {
      setRefetchLoading(false)
    }, 300)

    queryClient.invalidateQueries(KEY_OPPORTUNITIES_ACTIVE_BASE_LIST)
  }, [queryClient])

  const isEmpty = useMemo(() => !list?.length, [list])

  const highlights = list.filter((item) => item.highlighted)

  const highlightsIsEmpty = !highlights.length

  const handleGoToDetails = (opportunityId: string, itemType?: OPPORTUNITY_TYPE_ENUM) => {
    LogEvent(`oportunidades_base_ativa_acessar_detalhes`, {
      item_id: opportunityId
    })

    navigation.navigate('OpportunityDetails', {
      id: opportunityId,
      type: itemType
    })
  }

  const isMobile = useBreakpointValue({ base: true, lg: false })

  const toast = useToast()

  const [opportunityHighlightIsLoading, setOpportunityHighlightIsLoading] = useState('')

  const { mutateAsync: setOpportunityHighlighted } =
    useSetOpportunityActiveBaseHighlightedMutation()

  const { mutateAsync: setOpportunityUnHighlighted } =
    useSetOpportunityActiveBaseUnHighlightedMutation()

  const handleHighlight = (id: string, highlighted: boolean) => {
    LogEvent(`oportunidades_base_ativa_${highlighted ? 'desfazer_' : ''}destacar`, {
      item_id: id
    })

    setOpportunityHighlightIsLoading(id)

    return (
      id &&
      (highlighted ? setOpportunityUnHighlighted : setOpportunityHighlighted)(
        {
          id
        },
        {
          onError: () => {
            toast.show({
              render: () => <Toast type="error" text="Tente novamente mais tarde" />,
              duration: 3000
            })

            setOpportunityHighlightIsLoading('')
          },
          onSuccess: () => {
            refetch()

            setOpportunityHighlightIsLoading('')
          }
        }
      )
    )
  }

  const [opportunityConcludeIsLoading, setOpportunityConcludeIsLoading] = useState('')

  const { mutateAsync: setOpportunityFinished } = useSetFinishedOpportunityActiveBaseMutation()

  const { mutateAsync: setOpportunityUnFinished } = useSetUnFinishedOpportunityActiveBaseMutation()

  const handleConclude = (data: HandleFinishParams) => {
    const { finished, id, comment, insight, only_finish, score } = data

    LogEvent(`oportunidades_base_ativa_${finished ? 'desfazer_' : ''}concluir`, {
      item_id: id
    })

    setOpportunityConcludeIsLoading(id)

    return (
      id &&
      (finished ? setOpportunityUnFinished : setOpportunityFinished)(
        {
          id,
          only_finish,
          comment,
          insight,
          score
        },
        {
          onError: () => {
            toast.show({
              render: () => <Toast type="error" text="Tente novamente mais tarde" />,
              duration: 3000
            })

            setOpportunityConcludeIsLoading('')
          },
          onSuccess: () => {
            refetch()

            setOpportunityConcludeIsLoading('')
          }
        }
      )
    )
  }

  const activeFilterCount = calculateActiveFilterCount(allFilters)

  const handleGoToFilters = () => {
    LogEvent(`oportunidades_base_ativa_acessar_filtros`, {
      item_id: ''
    })

    navigation.navigate('OpportunityFilter', {
      listType: LIST_TYPE_ENUM.ActiveBase,
      offer: route?.params?.offer,
      public_rating: route?.params?.public_rating,
      segment: route?.params?.segment,
      network: route?.params?.network,
      subsidiary: route?.params?.subsidiary,
      opportunity_type: route?.params?.opportunity_type,
      user_std_code: route?.params?.user_std_code
    })
  }

  useEffect(() => {
    refetchHook()
  }, [search, refetchHook])

  return {
    opportunitiesList: list,
    isLoading,
    fetchMore,
    isFetchingNextPage,
    refetch,
    refreshing: refetchLoading,
    isEmpty,
    highlights,
    highlightsIsEmpty,
    handleHighlight,
    handleGoToDetails,
    isMobile,
    highlightIsLoading: opportunityHighlightIsLoading,
    handleConclude,
    concludeIsLoading: opportunityConcludeIsLoading,
    totalItems,
    handleGoToFilters,
    activeFilterCount
  }
}
